<template>
  <div>
    <SelectorPanel
      type="search"
      :text="labels.search"
      :disabled="disabled"
      :force-close="forceClose"
      @click="handlePanelOpen"
    >
      <template v-slot:append>
        <SearchDefinition :new-icon="true"></SearchDefinition>
      </template>
      <template>
        <v-container
          class="pa-0 hyp-search-container"
          :class="{
            'hyp-search-container-min': isMinimized,
            'hyp-search-container-full': !isMinimized,
          }"
        >
          <div class="pull-left hyp-search-filter-container">
            <SelectorPanelHeader
              type="search"
              :title="labels.search"
              class="pb-2"
              @click-close="handleClickClose"
            >
            </SelectorPanelHeader>

            <div class="reset-filter-holder pa-5">
              <v-btn
                small
                outlined
                block
                color="primary"
                @click="handleClickNewSearch"
                >New Search</v-btn
              >
            </div>

            <SearchContentFilters
              :date-range-start-days-back="dateRangeStartDaysBack"
              :date-range-max-date-back="dateRangeMaxDateBack"
              :filter-counts="filterCounts"
              :reset-timestamp="resetFilters"
              :current-content-type="contentType"
              :riskCategories="riskCategories"
              @change-keywords="handleChangeKeywords"
              @change-keywords-builder="handleChangeKeywordsBuilder"
              @change-date-range="handleChangeDateRange"
              @change-bounds="handleChangeBounds"
              @change-locations="handleChangeLocations"
              @change-places="handleChangePlaces"
              @change-events="handleChangeEvents"
              @change-events-v2="handleChangeEventsV2"
              @change-hotspots-filter="handleChangeHotspotsFilter"
              @change-sources="handleChangeSources"
              @change-has-filters-set="handleChangeHasFiltersSet"
              @reset="resetFilters = new Date().toISOString()"
              @change-should-pull-data="handleChangeShouldPullData"
              @change-tab="handleChangeEventType"
              @change-risk-categories="handleChangeRiskCategories"
              @change-severity="handleChangeSeverity"
              @change-risk-category-tags="handleChangeRiskCategoryTags"
            ></SearchContentFilters>
          </div>
          <div class="pull-left hyp-search-content-container">
            <SearchContent
              :refresh="refresh"
              :keywords="keywords"
              :keywords-builder="keywordsBuilder"
              :date-range-start-days-back="dateRangeStartDaysBack"
              :date-range="dateRange"
              :bounds="bounds"
              :bounds-type="boundsType"
              :aoi-id="aoiId"
              :places="places"
              :radius="radius"
              :poisDistUnits="poisDistUnits"
              :sources="sources"
              :events="events"
              :events-v2="eventsV2"
              :hotspotsOnly="hotspotsOnly"
              :is-minimized="isMinimized"
              :should-pull-data="shouldPullData"
              :risk-categories="riskCategories"
              :severity="severity"
              :has-deep-link="hasDeepLink"
              :risk-category-tags="riskCategoryTags"
              @filter-counts-update="handleFilterCountsUpdate"
              @click-minimize="handleClickMinimize"
              @click-close="handleClickClose"
              @click-show-notification-builder="showNotificationBuilder"
              @change-deep-link="changeDeepLink"
            ></SearchContent>
          </div>
          <div class="clearfix"></div>
        </v-container>
      </template>
    </SelectorPanel>

    <NotificationsStepper
      :show="notificationStepperShow"
      :notification-exists="notificationExists"
    ></NotificationsStepper>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import {
  EVENT_ACTIONS,
  SEARCH_CATEGORIES,
  CONTENT_TYPES,
} from "@/common/constants/analytics";

import { LABELS } from "@/common/constants/labels";
import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_USER,
} from "@/store";
import { GET_SAVED_SEARCH_DETAILS } from "@/store/actions.type";

import { getTodayStartEndDate, nowUtc } from "@/common/date.utilities";

export default {
  name: "SearchSelector",
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    SearchContentFilters: () =>
      import("@/components/search/SearchContentFilters"),
    SearchContent: () => import("@/components/search/SearchContent"),
    SearchDefinition: () => import("@/components/search/SearchDefinition"),
    NotificationsStepper: () =>
      import("@/components/notifications/NotificationsStepper"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      keywords: null,
      keywordsBuilder: null,
      dateRange: null,
      dateRangeStartDaysBack: 2,
      dateRangeMaxDateBack: null,
      boundsType: "geography",
      bounds: null,
      aoiId: null,
      places: null,
      radius: null,
      poisDistUnits: "mi",
      events: null,
      eventsV2: { eventType: "all" },
      hotspotsOnly: false,
      sources: null,
      isOpen: false,
      filterCounts: null,
      forceClose: null,
      isMinimized: false,
      refresh: new Date().toISOString(),
      hasFiltersSet: false,
      shouldPullData: false,
      resetFilters: new Date().toISOString(),
      notificationBuilderShow: false,
      notificationStepperShow: null,
      labels: LABELS,
      riskCategories: null,
      contentType: null, //Value is always one of CONTENT_TYPES
      searchid: null,
      notificationExists: false,
      severity: null,
      hasDeepLink: Boolean(lodashGet(this.$route, "params.searchid")),
      riskCategoryTags: null,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      resetMapIcons: MODULE_PREFIX_MAP + "resetMapIcons",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
    }),
  },
  mounted() {
    this.searchid = this.$route.params?.searchid;
  },
  methods: {
    changeDeepLink(val) {
      this.hasDeepLink = val;
    },
    handlePanelOpen(open) {
      lodashDebounce(() => {
        this.isOpen = open;
      }, 1000)();
    },
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    showNotificationBuilder() {
      this.notificationBuilderShow = true;
      this.notificationStepperShow = new Date().toISOString();
    },
    handleClickMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    handleChangeKeywords(keywords) {
      this.keywords = keywords;
      this.handleChangeShouldPullData(true);
    },
    handleChangeKeywordsBuilder(keywordsBuilder) {
      this.keywordsBuilder = keywordsBuilder === "" ? null : keywordsBuilder;
      this.handleChangeShouldPullData(true);
    },
    handleChangeDateRange(dates) {
      this.dateRange = dates;
      this.handleChangeShouldPullData(true);
    },
    handleChangeBounds(obj, dontPullData) {
      this.boundsType = obj.boundsType;
      this.bounds = obj.bounds;
      this.handleChangeShouldPullData(!dontPullData);
    },
    handleChangeLocations(params) {
      this.boundsType = params.boundsType;
      this.bounds = params.boundsType === "mapextent" ? params.bounds : null;
      this.poisDistUnits = params.poisDistUnits || "mi";
      this.places = params.places;
      this.radius = params.radius;
      this.aoiId = params.aoiId;
      this.handleChangeShouldPullData(true);
    },
    handleChangePlaces(params) {
      this.poisDistUnits = params.poisDistUnits;
      this.places = params.places;
      this.radius = params.radius;
      this.handleChangeShouldPullData(true);
    },
    handleClickNewSearch() {
      // reset dates and clear old search details from email deep links to /savedsearch/:id
      if (this.hasDeepLink) {
        const blankSearch = {
          params: {
            range: [-3, "-0"], // not sure what this is for
            start: getTodayStartEndDate(dayjs(nowUtc).add(-2, "days")).start,
            end: getTodayStartEndDate(dayjs(nowUtc)).end,
          },
        };

        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS,
          blankSearch
        );

        delete this.$route.query.start;
        delete this.$route.query.end;
        this.hasDeepLink = false;
        this.$router.push("/home");
      }

      this.resetFilters = new Date().toISOString();
      lodashDebounce(() => {
        this.handleChangeShouldPullData(false);
      }, 1000)();
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.clicked,
        "New Search"
      );
    },
    handleChangeEvents(events, eventType) {
      this.dateRangeMaxDateBack = null;
      this.events = events;
      this.handleChangeShouldPullData(true);
      this.eventsV2 = {
        eventType: eventType || "all",
        events: events,
      };
      this.dateRangeMaxDateBack = null;
      if (eventType === "verified") {
        this.dateRangeMaxDateBack = "2008-01-08T00:00:00.000Z";
      }
    },
    handleChangeEventsV2(events) {
      this.eventsV2 = events;
      this.dateRangeMaxDateBack = null;
      if (lodashGet(events, "eventType") === "verified") {
        this.dateRangeMaxDateBack = "2008-01-08T00:00:00.000Z";
      }
      const selectedTopics = events.events ? events.events.split(",") : null;
      if (selectedTopics && selectedTopics.length > 0) {
        this.contentType = CONTENT_TYPES.events;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeHotspotsFilter(params) {
      this.hotspotsOnly = params.hotspotsOnly;
      this.handleChangeShouldPullData(true);
    },
    handleChangeSources(sources) {
      this.sources = sources;
      this.contentType = sources.analysis
        ? CONTENT_TYPES.analysis
        : CONTENT_TYPES.events;
      this.handleChangeShouldPullData(true);
    },
    handleFilterCountsUpdate(filterCounts) {
      this.$set(this, "filterCounts", filterCounts);
    },
    handleChangeHasFiltersSet(val) {
      this.hasFiltersSet = val;
    },
    handleChangeShouldPullData(val) {
      this.shouldPullData = val;
    },
    handleChangeEventType(params) {
      this.eventsV2.eventType = params.eventType;
      if (params && params.length > 0) {
        this.contentType = CONTENT_TYPES.events;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeRiskCategories(params) {
      this.riskCategories = params;
      if (params && params.length > 0) {
        this.contentType = CONTENT_TYPES.analysis;
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeSeverity(params) {
      this.severity = params;
      if (params && params.length > 0) {
        this.handleChangeShouldPullData(true);
      }
    },
    handleChangeRiskCategoryTags(params) {
      this.riskCategoryTags = params;
      if (params && params.length > 0) {
        this.handleChangeShouldPullData(true);
      }
    },
  },
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.refresh = new Date().toISOString();
      }
    },
    "preferences.poisDistUnits"() {
      this.handleClickNewSearch();
    },
    resetMapIcons: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.handleClickNewSearch();
        }
      },
    },
    contentType: {
      handler(newVal) {
        if (newVal === CONTENT_TYPES.events) {
          this.riskCategories = null;
        } else {
          this.eventsV2.events = null;
        }
      },
    },
    savedSearchDetails: {
      handler() {
        this.notificationExists =
          this.savedSearchDetails &&
          this.savedSearchDetails.user_id ===
            this.savedSearchDetails.owner_id &&
          !!this.savedSearchDetails.frequency;
      },
    },
  },
  beforeDestroy() {
    this.isOpen = false;
  },
};
</script>

<style scoped>
.hyp-search-container {
  padding: 0 !important;
  display: grid;
  grid-template-columns: 345px auto;
}
.hyp-search-container-min {
  width: 395px;
}
.hyp-search-container-full {
  width: 723px;
}
.hyp-search-filter-container {
  box-sizing: border-box;
  height: 100vh;
  display: grid;
  grid-template-rows: 72px 40px 1fr;
  grid-row-gap: 10px;
}
.hyp-search-content-container {
  box-sizing: border-box;
  height: 100vh;
}
.reset-filter-holder {
  text-align: center;
  font-size: 14px;
  width: 100%;
}
</style>
